import React, { useState, useRef, useEffect } from "react";
import { FormGroup, Label, Button } from "reactstrap";
import DatePicker from "./DatePicker.tsx";
import FilterInput from "./FilterInput.tsx";
import "../../styles/Filter.css";
import AutoCompleteDropDown from "./AutoCompleteDropDown.tsx";
import Cancel from "../../assets/images/cancel.svg";
import {
  ALL_PRODUCT,
  ALL_CROPS,
  COUNTRY,
  CLAIMS_PROCESSED_CATEGORIES,
} from "../../utils/Constants.tsx";
import {
  getLevelOne,
  getLevelTwo,
  getProducts,
  getSeasons,
} from "../../services/FilterAPI.tsx";
import { useDispatch, useSelector } from "react-redux";
import SeasonMultiSelect from "../UI/SeasonMultiSelect.tsx";
import { OptionType } from "../../utils/Types.tsx";
import { getComputedDate } from "../../services/MapApi.tsx";

const currentYear = new Date().getFullYear();
const yearList = Array.from({ length: currentYear - 2018 + 1 }, (_, i) =>
  (2018 + i).toString(),
);

interface StateFilterProps {
  formData: any;
  stateList: string[];
  districtList: string[];
  gridId: string;
  handleStateChange: (state: string) => void;
  handleDistrictChange: (disrict: string) => void;
  handleProductChange?: (product: string) => void;
  handleSeasonChange?: (season: string) => void;
  handleDateChange: (field: string, value: string) => void;
  handleLevel1Change?: (level1: string) => void;
  handleLevel2Change?: (level2: string) => void;
  handleLevel3Change: (level3: string) => void;
  handleChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { target: { name: string; value: any; type?: string } },
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  statePlaceholder: string;
  districtPlaceholder: string;
  level3Placeholder: string;
  clearFilters: () => void;
  handleSubmit: () => void;
  clearGridId: () => void;
  levelOne?: string;
  levelTwo?: string;
  levelThree?: string;
  product?: string;
  season?: string;
  minDate: Date | string | null;
  maxDate: Date | string | null;

  // Dependent sub-dropdown category

  claimsCategory: string | null;
  setClaimsCategory: (v: string | null) => void;
}

const StateFilter: React.FC<StateFilterProps> = ({
  formData,
  stateList,
  districtList,
  gridId,
  handleStateChange,
  handleDistrictChange,
  handleDateChange,
  handleChange,
  handleLevel3Change,
  setFormData,
  statePlaceholder,
  districtPlaceholder,
  level3Placeholder,
  clearFilters,
  handleSubmit,
  clearGridId,
  levelOne,
  levelTwo,
  levelThree,
  product,
  season,
  minDate,
  maxDate,
  claimsCategory,
  setClaimsCategory,
}) => {
  const [searchState, setSearchState] = useState<string>("");
  const [searchDistrict, setSearchDistrict] = useState<string>("");
  const [searchLevelThree, setSearchLevelThree] = useState<string>("");
  const [filteredStates, setFilteredStates] = useState<string[]>(stateList);
  const [filteredDistrict, setFilteredDistrict] =
    useState<string[]>(districtList);
  const [filteredLevelThree, setFilteredLevelThree] = useState<string[]>([]);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [isDistrictDropdownOpen, setIsDistrictDropdownOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isLevel1MarkerOpen, setLevel1MarkerOpen] = useState(false);
  const [isLevel2MarkerOpen, setLevel2MarkerOpen] = useState(false);
  const [isLevel3MarkerOpen, setLevel3MarkerOpen] = useState(false);
  // 🔹 NEW: inline sub-dropdown open state for Category (inside Level-2 dropdown)

  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const districtDropdownRef = useRef<HTMLDivElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);
  const level1DropdownRef = useRef<HTMLDivElement>(null);
  const level2DropdownRef = useRef<HTMLDivElement>(null);
  const level3DropdownRef = useRef<HTMLDivElement>(null);

  const [productList, setProductList] = useState<string[]>([]);
  const [seasonList, setSeasonList] = useState<OptionType[]>([]);
  const [levelOneList, setLevelOneList] = useState<string[]>([]);
  const [levelThreeList, setLevelThreeList] = useState<string[]>([]);

  const dispatch: any = useDispatch();
  const productListWithId = useSelector(
    (state: any) => state?.filter?.products,
  );
  const seasonListWithId = useSelector((state: any) => state?.filter?.seasons);
  const levelOneListWithId = useSelector(
    (state: any) => state?.filter?.levelOne,
  );
  const levelTwoList = useSelector((state: any) => state?.filter?.levelTwo);
  const levelThreeListWithId = useSelector(
    (state: any) => state?.filter?.levelThree,
  );

  const isConsolidated =
    formData?.levelOne === "Consolidated Business" ||
    formData?.levelOne === "Consolidated Manpower";

  const isMultiSeason =
    Array.isArray(formData?.season) && formData.season.length > 1;

  const isYearSelected = !!formData?.year;

  const getLabelList = (list: any, labelKey: string) => {
    const labelList: string[] = [];
    if (list && list?.length > 0) {
      list?.forEach((element: any) => {
        labelList.push(element[labelKey]);
      });
    }
    return labelList;
  };

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getSeasons());
    dispatch(getLevelOne());
    dispatch(getComputedDate());
  }, [dispatch]);

  useEffect(() => {
    if (productListWithId?.length) {
      setProductList([
        ALL_PRODUCT,
        ...getLabelList(productListWithId, "productname"),
      ]);
    }
    if (seasonListWithId?.length) {
      setSeasonList(
        seasonListWithId?.map((item: any) => ({
          value: String(item?.seasonid),
          label: item?.seasonname,
        })),
      );
    }
    if (levelOneListWithId?.length) {
      const levelOneListLocal = [...levelOneListWithId];
      setLevelOneList(getLabelList(levelOneListLocal, "name"));
    } else {
      setLevelOneList([]);
    }
    if (levelThreeListWithId?.length) {
      const list = getLabelList(levelThreeListWithId, "crop_name");
      setLevelThreeList([...[ALL_CROPS], ...list]);
      setFilteredLevelThree([...[ALL_CROPS], ...list]);
      handleChange({ target: { name: "levelThree", value: ALL_CROPS } });
    } else {
      setLevelThreeList([ALL_CROPS]);
      setFilteredLevelThree([ALL_CROPS]);
      handleChange({ target: { name: "levelThree", value: ALL_CROPS } });
    }
  }, [
    productListWithId,
    seasonListWithId,
    levelOneListWithId,
    levelThreeListWithId,
  ]);

  // Close dropdowns when clicking outside (also close sub-dropdown)
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        stateDropdownRef.current &&
        !stateDropdownRef.current.contains(event.target as Node)
      ) {
        setIsStateDropdownOpen(false);
      }
      if (
        districtDropdownRef.current &&
        !districtDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDistrictDropdownOpen(false);
      }
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProductDropdownOpen(false);
      }
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target as Node)
      ) {
        setIsYearDropdownOpen(false);
      }
      if (
        level1DropdownRef.current &&
        !level1DropdownRef.current.contains(event.target as Node)
      ) {
        setLevel1MarkerOpen(false);
      }
      if (
        level2DropdownRef.current &&
        !level2DropdownRef.current.contains(event.target as Node)
      ) {
        setLevel2MarkerOpen(false);
      }
      if (
        level3DropdownRef.current &&
        !level3DropdownRef.current.contains(event.target as Node)
      ) {
        setLevel3MarkerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (gridId && formData.state !== "") {
      setFormData((prevState: any) => ({ ...prevState, gridNumber: gridId }));
    }
  }, [gridId, setFormData]);

  // Filters for search
  useEffect(() => {
    if (stateList?.length > 0) {
      setFilteredStates(
        stateList.filter((state) =>
          state.toLowerCase().includes(searchState.toLowerCase()),
        ),
      );
    }
  }, [searchState, stateList]);

  useEffect(() => {
    if (districtList?.length > 0) {
      setFilteredDistrict(
        districtList.filter((distr) =>
          distr.toLowerCase().includes(searchDistrict.toLowerCase()),
        ),
      );
    }
  }, [searchDistrict, districtList]);

  useEffect(() => {
    if (levelThreeList?.length > 0) {
      setFilteredLevelThree(
        levelThreeList.filter((item) =>
          item.toLowerCase().includes(searchLevelThree.toLowerCase()),
        ),
      );
    }
  }, [searchLevelThree, levelThreeList]);

  useEffect(() => setSearchState(""), [statePlaceholder]);
  useEffect(() => setSearchDistrict(""), [districtPlaceholder]);
  useEffect(() => setSearchLevelThree(""), [level3Placeholder]);
  useEffect(() => {
    // ensure distance resets when markers change
    handleChange({ target: { name: "distance", value: "0", type: "range" } });
  }, [formData.markers]);

  const onLevelOneChange = (e: any) => {
    const value = e?.target?.value;
    if (value) {
      const selectedLevelOne = levelOneListWithId.filter(
        (item: any) => item.name === value,
      );
      if (selectedLevelOne.length > 0) {
        dispatch(getLevelTwo({ id: selectedLevelOne[0].id }));
      }
    }

    // Reset Level2 & Season when Level1 changes
    handleChange({ target: { name: "levelTwo", value: "" } });

    handleChange({ target: { name: "season", value: [] } });

    if (value !== "Claims") {
      setClaimsCategory(null);
    }
  };
  const showInlineCategory =
    formData?.levelOne === "Claims" && formData?.levelTwo === "Claim Processed";

  const setLevel2DropdownSafe = (open: boolean) => {
    if (isConsolidated) return;
    setLevel2MarkerOpen(open);
  };

  return (
    <>
      <div className="row">
        {/* Level1 Dropdown */}
        <div className="col-sm-2">
          <FilterInput
            label="Level 1"
            placeholder="Select Level 1"
            dropdown={isLevel1MarkerOpen}
            setDropdown={setLevel1MarkerOpen}
            formDataValue={formData.levelOne || ""}
            valueName="levelOne"
            inputArray={levelOneList}
            isRequired={
              formData?.levelOne === "Consolidated Business" ||
              formData?.levelOne === "Consolidated Manpower"
                ? false
                : true
            }
            change={(e) => {
              onLevelOneChange(e);
              handleChange(e);
            }}
            dropdownRef={level1DropdownRef}
            clearable={true}
          />
        </div>

        {/* Level2 Dropdown (DISABLED for Consolidated) */}
        <div className={`col-sm-2 ${isConsolidated ? "disabled-opacity" : ""}`}>
          <FilterInput
            label="Level 2"
            placeholder="Select Level 2"
            dropdown={isLevel2MarkerOpen}
            setDropdown={setLevel2DropdownSafe}
            formDataValue={formData.levelTwo || ""}
            valueName="levelTwo"
            inputArray={
              isConsolidated ? [] : formData.levelOne ? levelTwoList : []
            }
            isRequired={
              formData?.levelOne === "Consolidated Business" ||
              formData?.levelOne === "Consolidated Manpower"
                ? false
                : true
            }
            change={(e) => {
              if (isConsolidated) return;
              handleChange(e);
            }}
            dropdownRef={level2DropdownRef}
            clearable={true}
          />
        </div>

        {/*  State new dropDown */}
        <div className="col">
          <AutoCompleteDropDown
            label="State/Country"
            dropdownRef={stateDropdownRef}
            isDropdownOpen={isStateDropdownOpen}
            placeholder={
              formData.state === COUNTRY ? COUNTRY : statePlaceholder
            }
            filteredArray={filteredStates}
            formValue={formData.state}
            searchValue={searchState}
            setSearchValue={setSearchState}
            setIsDropdownOpen={setIsStateDropdownOpen}
            handleChange={handleStateChange}
            isRequired={
              formData?.levelOne === "Consolidated Business" ||
              formData?.levelOne === "Consolidated Manpower"
                ? false
                : true
            }
          />
        </div>

        {/* District Dropdown */}
        {formData.state !== COUNTRY && (
          <div className="col">
            <AutoCompleteDropDown
              label="District"
              dropdownRef={districtDropdownRef}
              isDropdownOpen={isDistrictDropdownOpen}
              placeholder={districtPlaceholder}
              filteredArray={filteredDistrict}
              formValue={formData.district}
              searchValue={searchDistrict}
              setSearchValue={setSearchDistrict}
              setIsDropdownOpen={setIsDistrictDropdownOpen}
              handleChange={handleDistrictChange}
              clearable={true}
            />
          </div>
        )}

        {/* Product Dropdown */}
        <div className="col">
          <FilterInput
            label="Product"
            placeholder="Select Product"
            dropdown={isProductDropdownOpen}
            setDropdown={setIsProductDropdownOpen}
            formDataValue={formData?.product || ""}
            valueName="product"
            inputArray={productList}
            isRequired={
              formData?.levelOne === "Consolidated Business" ||
              formData?.levelOne === "Consolidated Manpower"
                ? false
                : true
            }
            change={(e) => handleChange(e)}
            dropdownRef={productDropdownRef}
            clearable={true}
          />
        </div>

        {/* Season Dropdown */}
        <div className="col">
          <SeasonMultiSelect
            label="Season"
            options={seasonList}
            value={formData?.season || []} // array
            onChange={(e: OptionType[]) =>
              handleChange({ target: { name: "season", value: e } })
            }
            isMulti={
              formData.levelOne === "Policy" || formData.levelOne === "Claims"
            }
          />
        </div>

        {/* Year Dropdown */}
        <div className="col">
          <FilterInput
            label="Year"
            placeholder="Select Year"
            dropdown={isYearDropdownOpen}
            setDropdown={setIsYearDropdownOpen}
            formDataValue={formData?.year || ""}
            valueName="year"
            inputArray={yearList}
            isRequired={formData.season?.length > 0}
            change={(e) => handleChange(e)}
            dropdownRef={yearDropdownRef}
            clearable={true}
          />
        </div>
      </div>

      <div className="row">
        {/* DatePickers */}
        <div className="col">
          <DatePicker
            label="From Date"
            isRequired={true}
            value={
              formData.levelTwo !== "Arable" && formData.levelTwo !== "POSP"
                ? formData.stateStartDate
                : ""
            }
            minDate={minDate}
            maxDate={formData.stateEndDate}
            onChange={(date) => handleDateChange("stateStartDate", date)}
            disabled={
              formData.levelTwo === "Arable" ||
              formData.levelTwo === "POSP" ||
              (isMultiSeason && !isYearSelected)
            }
          />
        </div>

        <div className="col">
          <DatePicker
            label="To Date"
            isRequired={true}
            value={
              formData.levelTwo !== "Arable" && formData.levelTwo !== "POSP"
                ? formData.stateEndDate
                : ""
            }
            minDate={formData.stateStartDate}
            maxDate={maxDate}
            onChange={(date) => handleDateChange("stateEndDate", date)}
            disabled={
              formData.levelTwo === "Arable" ||
              formData.levelTwo === "POSP" ||
              (isMultiSeason && !isYearSelected)
            }
          />
        </div>

        {/* Level3 Dropdown */}
        <div className="col">
          <AutoCompleteDropDown
            label="Level 3"
            dropdownRef={level3DropdownRef}
            isDropdownOpen={isLevel3MarkerOpen}
            placeholder={level3Placeholder}
            filteredArray={filteredLevelThree}
            formValue={formData.levelThree}
            searchValue={searchLevelThree}
            setSearchValue={setSearchLevelThree}
            setIsDropdownOpen={setLevel3MarkerOpen}
            handleChange={(value) =>
              handleChange({ target: { name: "levelThree", value } })
            }
            isRequired={
              formData?.levelOne === "Consolidated Business" ||
              formData?.levelOne === "Consolidated Manpower"
                ? false
                : true
            }
            disabled={
              formData?.levelTwo === "Target" ||
              formData.levelTwo === "Arable" ||
              formData.levelTwo === "POSP"
            }
          />
        </div>

        <div
          className={`col-sm-2 ${
            formData?.levelOne === "Consolidated Business" ||
            formData?.levelOne === "Consolidated Manpower"
              ? "disabled-opacity"
              : ""
          }`}
        >
          <FormGroup className="formgroup">
            <Label htmlFor="gridNumber" className="custom-label">
              Grid ID
            </Label>
            <div className="dropdown-button grid-input-parent">
              <input
                type="text"
                name="gridNumber"
                id="gridNumber"
                value={formData.gridNumber}
                onChange={handleChange}
                className="inputbox dropdown-input"
                placeholder="Select Grid On The Map"
                readOnly
              />
              {formData.gridNumber && (
                <img
                  alt="clear"
                  src={Cancel}
                  className="dropdown-button-child grid-clear"
                  onClick={clearGridId}
                />
              )}
            </div>
          </FormGroup>
        </div>
        {/* Distance Slider */}
        <div
          className={`col-sm-2 ${
            formData?.levelOne === "Consolidated Business" ||
            formData?.levelOne === "Consolidated Manpower"
              ? "disabled-opacity"
              : ""
          }`}
        >
          <FormGroup className="formgroup">
            <Label className="custom-label mt-2" htmlFor="distance">
              Distance (in Kms)
            </Label>
            <div className="slider-container">
              <input
                type="range"
                id="distance"
                name="distance"
                min="0"
                max="10"
                step="1"
                value={formData.distance}
                onChange={handleChange}
                disabled={
                  formData?.levelOne === "Consolidated Business" ||
                  formData?.levelOne === "Consolidated Manpower"
                }
                style={{
                  background: `linear-gradient(to right, #4a90e2 ${
                    (formData.distance / 10) * 100
                  }%, #ddd ${(formData.distance / 10) * 100}%)`,
                }}
                className="slider cursor-action"
              />
              <div className="slider-labels">
                {Array.from({ length: 11 }, (_, index) => (
                  <span key={index} className="slider-label">
                    {index}
                  </span>
                ))}
              </div>
            </div>
          </FormGroup>
        </div>

        <div className="col-sm-2 d-flex mt-4">
          <Button
            color="primary"
            className="submitbutton"
            onClick={handleSubmit}
          >
            Submit
          </Button>
          <Button className="clearbutton ms-2" onClick={clearFilters}>
            Clear
          </Button>
        </div>
      </div>
    </>
  );
};

export default StateFilter;
